import { defineStore } from 'pinia'

export const usePodcastStore = defineStore('podcast', {
	state: () => ({
		podcasts: [],
		news: [],
		episodes: [],
		events: [],
		discussions: [],
		podcastDetails: {},
		currentEpisode: null,
		currentPodcast: null,
		currentComment: null,
		isPlaying: false,
		isLoading: false,
		error: null,
		apiUrl: 'https://proxy.noden.forum',
		forumUrl: 'https://www.noden.forum',
		playbackPositions: JSON.parse(localStorage.getItem('podcast_positions')) || {},
		lastPlayedEpisodeId: localStorage.getItem('last_played_episode_id') || null,
		lastPlayedPodcastId: localStorage.getItem('last_played_podcast_id') || null,
		favoritePodcastIds: JSON.parse(localStorage.getItem('favorite_podcast_ids')) || []
	}),
	getters: {
		getPodcastById: (state) => (id) => {
			return state.podcasts.find(p => p.id === id)
		},
		getPodcastByEpisodeLinkid: (state) => (episodeLinkid) => {
			const episode = state.episodes.find(e => e.linkid === episodeLinkid)
			return episode?.pod || null
		},
		getPodcastDetailsById: (state) => (id) => {
			return state.podcastDetails[id] || null
		},
		getEpisodeById: (state) => (episodeLinkid) => {
			for (const podcastId in state.podcastDetails) {
				const podcast = state.podcastDetails[podcastId]
				const episode = podcast.episodes.find(e => e.linkid === episodeLinkid)
				if (episode) return { episode, podcast }
			}
			return null
		},
		getSavedTimeById: (state) => (episodeLinkid) => {
			return state.playbackPositions[episodeLinkid] || 0
		},
		isFavorite: (state) => (podcastId) => {
			return state.favoritePodcastIds.includes(podcastId)
		}
	},
	actions: {
		async fetchNews() {
			if (this.news.length > 0) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/mediakollen.xml `)
				if (!response.ok) throw new Error(`Kunde inte hämta nyheter: ${response.status}`)
				
				const xmlText = await response.text()
				const parser = new DOMParser()
				const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
				const parseError = xmlDoc.querySelector('parsererror')
				if (parseError) throw new Error('Kunde inte tolka RSS/XML-data')
				
				const items = xmlDoc.querySelectorAll('item')
				
				this.news = Array.from(items).map(item => {
					return {
						title: item.querySelector('title')?.textContent || '',
						link: item.querySelector('link')?.textContent || '',
						description: item.querySelector('description')?.textContent || '',
						category: item.querySelector('category')?.textContent || '',
						date: item.querySelector('pubDate')?.textContent || ''
					}
				})
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		async fetchDiscussions() {
			if (this.discussions.length > 0) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/app/gnistor.json`)
				if (!response.ok) throw new Error(`Kunde inte hämta gnistor: ${response.status}`)
				this.events = await response.json()
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		async fetchEvents() {
			if (this.events.length > 0) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/api/discussions`)
				if (!response.ok) throw new Error(`Kunde inte hämta forumdiskussioner: ${response.status}`)
				this.discussions = await response.json()
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		async fetchPodcasts() {
			if (this.podcasts.length > 0) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/app/pods.json`)
				if (!response.ok) throw new Error(`Kunde inte hämta poddalistan: ${response.status}`)
				this.podcasts = await response.json()
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		async fetchEpisodes() {
			if (this.episodes.length > 0) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/app/episodes.json`)
				if (!response.ok) throw new Error(`Kunde inte hämta avsnittslistan: ${response.status}`)
				this.episodes = await response.json()
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		async fetchComments(link) {
			try {
				const response = await fetch(`${this.apiUrl}/api/discussions?filter[linkposter_url]=${encodeURIComponent(link)}`)
				if (!response.ok) throw new Error(`Kunde inte kommentarr: ${response.status}`)
				const res = await response.json()
				
				// Kontrollera att res.data finns, är en array och inte är tom
				if (res.data && Array.isArray(res.data) && res.data.length > 0) {
					// Sätt till det första objektet i arrayen
					this.currentComment = res.data[0]
				} else {
					this.currentComment = null
				}
			} catch (err) {
				this.error = err.message
			}
		},
		async fetchPodcastDetails(podcastId) {
			if (this.podcastDetails[podcastId]) return
			this.isLoading = true
			this.error = null
			try {
				const response = await fetch(`${this.apiUrl}/app/pods/${podcastId}.json`)
				if (!response.ok) throw new Error(`Kunde inte hämta avsnitt: ${response.status}`)
				const data = await response.json()
				this.podcastDetails[podcastId] = data
			} catch (err) {
				this.error = err.message
			} finally {
				this.isLoading = false
			}
		},
		playEpisode(episode, podcast) {
			this.currentEpisode = episode
			this.currentPodcast = podcast
			this.isPlaying = true
			
			this.lastPlayedEpisodeId = episode.linkid
			this.lastPlayedPodcastId = podcast.id
			
			localStorage.setItem('last_played_episode_id', episode.linkid)
			localStorage.setItem('last_played_podcast_id', podcast.id)
		},
		playNextEpisode() {
			if (!this.currentEpisode || !this.currentPodcast) {
				console.warn("Inget avsnitt spelas för tillfället.")
				return
			}
			
			const podcastId = this.currentPodcast.id
			const currentEpisodeLinkId = this.currentEpisode.linkid
			const podcastData = this.podcastDetails[podcastId]
			if (!podcastData || !podcastData.episodes) {
				console.warn("Kunde inte hitta avsnittslistan för denna podd i podcastDetails.")
				return
			}
			
			const episodesList = podcastData.episodes
			const currentIndex = episodesList.findIndex(e => e.linkid === currentEpisodeLinkId)
			
			if (currentIndex === -1) {
				console.warn("Kunde inte hitta det aktuella avsnittet i listan.")
				return
			}
			const nextIndex = currentIndex - 1
			
			if (nextIndex >= 0 && nextIndex < episodesList.length) {
				const nextEpisode = episodesList[nextIndex]
				this.playEpisode(nextEpisode, this.currentPodcast)
			} else {
				console.log("Detta är det sista avsnittet i listan.")
			}
		},
		playPreviousEpisode() {
			if (!this.currentEpisode || !this.currentPodcast) {
				console.warn("Inget avsnitt spelas för tillfället.")
				return
			}
			
			const podcastId = this.currentPodcast.id
			const currentEpisodeLinkId = this.currentEpisode.linkid
			const podcastData = this.podcastDetails[podcastId]
			if (!podcastData || !podcastData.episodes) {
				console.warn("Kunde inte hitta avsnittslistan för denna podd i podcastDetails.")
				return
			}
			
			const episodesList = podcastData.episodes
			const currentIndex = episodesList.findIndex(e => e.linkid === currentEpisodeLinkId)
			if (currentIndex === -1) {
				console.warn("Kunde inte hitta det aktuella avsnittet i listan.")
				return
			}
			
			const prevIndex = currentIndex + 1 
			if (prevIndex >= 0 && prevIndex < episodesList.length) {
				const prevEpisode = episodesList[prevIndex]
				this.playEpisode(prevEpisode, this.currentPodcast)
			} else {
				console.log("Detta var det första avsnittet i listan.")
				this.isPlaying = false
			}
		},
		restoreLastPlayed(episode, podcast) {
			this.currentEpisode = episode.episode
			this.currentPodcast = podcast
			this.isPlaying = false
		},
		togglePlay() {
			if (this.currentEpisode) this.isPlaying = !this.isPlaying
		},
		savePlaybackPosition(episodeLinkid, seconds) {
			this.playbackPositions[episodeLinkid] = Math.floor(seconds)
			localStorage.setItem('podcast_positions', JSON.stringify(this.playbackPositions))
		},
		toggleFavorite(podcastId) {
			const index = this.favoritePodcastIds.indexOf(podcastId)
			
			if (index === -1) {
				this.favoritePodcastIds.push(podcastId)
			} else {
				this.favoritePodcastIds.splice(index, 1)
			}
			
			localStorage.setItem('favorite_podcast_ids', JSON.stringify(this.favoritePodcastIds))
		}
	}
})
